import {Dialog, Form, FormField, showNotification} from 'platform/components';
import {VStack} from 'platform/foundation';
import {object, string} from 'yup';

import {ApiError, suffixTestId, TestIdProps} from 'shared/web';

import {usePriceReport} from '../../../../../hooks/usePriceReport';
import i18n from '../../../../../i18n/i18n';
import {useUserComplaintMutation} from '../../../../../store/omneticApi';
import {UserComplaintRequestBody} from '../../../../../types/api/api';
import {handleApiError} from '../../../../../utils/handleApiError';

type UserComplaintFormValues = {text: string; email: string};

interface UserComplaintDialogProps extends TestIdProps {
  isOpen: boolean;
  onClose: () => void;
}

export function UserComplaintDialog(props: UserComplaintDialogProps) {
  const {priceReport} = usePriceReport();
  const [userComplaint, {isLoading}] = useUserComplaintMutation();

  const onSubmit = async (values: UserComplaintFormValues) => {
    if (!priceReport?.id) {
      throw new Error('Report id is not defined');
    }
    const arg: UserComplaintRequestBody = {
      priceReportId: priceReport.id,
      url: window.location.href,
      ...values,
    };
    await userComplaint(arg)
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('entity.errorReportDialog.labels.successMessage'));
        props.onClose();
      })
      .catch((error: ApiError) => {
        handleApiError(error, {silent: true});
        showNotification.error(i18n.t('entity.errorReportDialog.labels.failureMessage'));
      });
  };

  const schema = object().shape({
    text: string().required(i18n.t('entity.errorReportDialog.validation.textRequired')),
    email: string()
      .nullable()
      .required(i18n.t('entity.errorReportDialog.validation.emailRequired'))
      .email(i18n.t('entity.errorReportDialog.validation.emailFormat')),
  });

  return (
    <Form onSubmit={onSubmit} schema={schema}>
      {(control, formApi) => (
        <Dialog
          title={i18n.t('entity.errorReportDialog.labels.title')}
          size="small"
          isOpen={props.isOpen}
          onClose={props.onClose}
          onCloseComplete={props.onClose}
          buttons={[
            {
              title: i18n.t('general.actions.cancel'),
              variant: 'secondary',
              onClick: props.onClose,
            },
            {
              title: i18n.t('general.actions.send'),
              onClick: formApi.handleSubmit(onSubmit),
              isLoading,
            },
          ]}
          data-testid={suffixTestId('complaintDialog', props)}
        >
          <VStack spacing={4}>
            <FormField
              name="text"
              type="textarea"
              control={control}
              label={i18n.t('entity.errorReportDialog.labels.description')}
              rows={5}
              isRequired
              isResizable
              data-testid={suffixTestId('text', props)}
            />
            <FormField
              name="email"
              type="email"
              control={control}
              label={i18n.t('entity.errorReportDialog.labels.email')}
              isRequired
              data-testid={suffixTestId('email', props)}
            />
          </VStack>
        </Dialog>
      )}
    </Form>
  );
}
